import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useSearchParams } from 'react-router'

import { endpoints } from '@/api'
import { keys } from '@/api/keys'
import { useCapabilities, useCan } from '@/auth'
import type { Location } from '@/api/types'
import {
  ALL_LOCATIONS,
  resolveSelection,
  useLocationStore,
  visibleLocations,
} from './location-store'

const PARAM = 'location_id'

export interface LocationScope {
  readonly locations: readonly Location[]
  readonly selectedId: string | null
  readonly selected: Location | null
  readonly canChooseAll: boolean
  readonly isLoading: boolean
  readonly error: unknown
  select: (id: string) => void
}

/**
 * Loads `/locations`, restricts it to the caller's scope, keeps the selection in the store AND in
 * `?location_id=` so list pages are shareable (FRONTEND_PLAN §7.5).
 */
export function useLocationScope(): LocationScope {
  const capabilities = useCapabilities()
  const canRead = useCan('locations:read')
  const [params, setParams] = useSearchParams()
  const selectedId = useLocationStore((state) => state.selectedId)
  const select = useLocationStore((state) => state.select)

  const query = useQuery({
    queryKey: keys.locations('true'),
    queryFn: () => endpoints.locations.list('true'),
    enabled: canRead,
    staleTime: 5 * 60_000,
  })

  const locations = visibleLocations(query.data ?? [], capabilities)
  const fromUrl = params.get(PARAM)

  useEffect(() => {
    if (query.data === undefined) return
    const next = resolveSelection(fromUrl ?? selectedId, locations, capabilities)
    if (next !== null && next !== selectedId) select(next)
  }, [query.data, fromUrl, selectedId, locations, capabilities, select])

  return {
    locations,
    selectedId,
    selected: locations.find((l) => l.id === selectedId) ?? null,
    canChooseAll: capabilities !== null && !capabilities.locationScoped,
    isLoading: query.isPending && canRead,
    error: query.error,
    select: (id) => {
      select(id)
      setParams(
        (current) => {
          const next = new URLSearchParams(current)
          if (id === ALL_LOCATIONS) next.delete(PARAM)
          else next.set(PARAM, id)
          return next
        },
        { replace: true },
      )
    },
  }
}
